import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fieldCls = "w-full bg-[#0B132B] border border-[#1E293B] rounded-md px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#F97316] transition-colors";
const emptySvc = { title: "", category_slug: "", description: "", features: "", turnaround: "", published: true };
const emptyCat = { name: "", description: "", icon: "truck", order: 99 };

export default function ServicesTab() {
  const [categories, setCategories] = useState([]);
  const [editingSvc, setEditingSvc] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [svcForm, setSvcForm] = useState(emptySvc);
  const [catForm, setCatForm] = useState(emptyCat);

  const load = () => api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openSvc = (svc, catSlug) => {
    setEditingSvc(svc ? svc.id : "new");
    setSvcForm(svc
      ? { title: svc.title, category_slug: svc.category_slug, description: svc.description, features: (svc.features || []).join("\n"), turnaround: svc.turnaround, published: svc.published }
      : { ...emptySvc, category_slug: catSlug || categories[0]?.slug || "" });
  };

  const saveSvc = async () => {
    const payload = { ...svcForm, features: svcForm.features.split("\n").map((f) => f.trim()).filter(Boolean) };
    try {
      if (editingSvc === "new") await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${editingSvc}`, payload);
      toast.success("Service saved");
      setEditingSvc(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const deleteSvc = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const openCat = (cat) => {
    setEditingCat(cat ? cat.id : "new");
    setCatForm(cat ? { name: cat.name, description: cat.description, icon: cat.icon, order: cat.order } : emptyCat);
  };

  const saveCat = async () => {
    try {
      if (editingCat === "new") await api.post("/admin/categories", catForm);
      else await api.put(`/admin/categories/${editingCat}`, catForm);
      toast.success("Category saved");
      setEditingCat(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Delete this category AND all its services?")) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-6" data-testid="services-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold text-white">Services & Categories</h2>
        <div className="flex gap-2">
          <button onClick={() => openCat(null)} data-testid="add-category-btn"
            className="flex items-center gap-1.5 text-xs border border-[#1E293B] rounded-md px-3 py-2 text-slate-300 hover:border-[#F97316]/50 hover:text-[#F97316] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Category
          </button>
          <button onClick={() => openSvc(null)} data-testid="add-service-btn"
            className="flex items-center gap-1.5 text-xs bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold rounded-md px-3 py-2 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Service
          </button>
        </div>
      </div>

      {editingCat && (
        <div className="bg-[#131C38] border border-[#F97316]/40 rounded-lg p-5 mb-6" data-testid="category-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">{editingCat === "new" ? "New Category" : "Edit Category"}</h3>
            <button onClick={() => setEditingCat(null)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
              placeholder="Category name" className={fieldCls} data-testid="cat-name-input" />
            <select value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })}
              className={fieldCls} data-testid="cat-icon-select">
              {["truck", "fuel", "file-check", "building", "calculator", "shield", "radio", "wrench"].map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <textarea value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
            placeholder="Short description" rows={2} className={`${fieldCls} mt-3`} data-testid="cat-desc-input" />
          <button onClick={saveCat} data-testid="cat-save-btn"
            className="mt-4 bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-xs px-5 py-2.5 rounded-md transition-colors">Save Category</button>
        </div>
      )}

      {editingSvc && (
        <div className="bg-[#131C38] border border-[#F97316]/40 rounded-lg p-5 mb-6" data-testid="service-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">{editingSvc === "new" ? "New Service" : "Edit Service"}</h3>
            <button onClick={() => setEditingSvc(null)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={svcForm.title} onChange={(e) => setSvcForm({ ...svcForm, title: e.target.value })}
              placeholder="Service title" className={fieldCls} data-testid="svc-title-input" />
            <select value={svcForm.category_slug} onChange={(e) => setSvcForm({ ...svcForm, category_slug: e.target.value })}
              className={fieldCls} data-testid="svc-category-select">
              {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <textarea value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })}
            placeholder="Description" rows={3} className={`${fieldCls} mt-3`} data-testid="svc-desc-input" />
          <textarea value={svcForm.features} onChange={(e) => setSvcForm({ ...svcForm, features: e.target.value })}
            placeholder={"What's included — one per line"} rows={3} className={`${fieldCls} mt-3`} data-testid="svc-features-input" />
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <input value={svcForm.turnaround} onChange={(e) => setSvcForm({ ...svcForm, turnaround: e.target.value })}
              placeholder="Turnaround (e.g. 2-3 business days)" className={fieldCls} data-testid="svc-turnaround-input" />
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={svcForm.published} data-testid="svc-published-toggle"
                onChange={(e) => setSvcForm({ ...svcForm, published: e.target.checked })}
                className="accent-[#F97316] w-4 h-4" /> Published
            </label>
          </div>
          <button onClick={saveSvc} data-testid="svc-save-btn"
            className="mt-4 bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-xs px-5 py-2.5 rounded-md transition-colors">Save Service</button>
        </div>
      )}

      <div className="space-y-6">
        {categories.map((cat) => (
          <div key={cat.id} className="bg-[#131C38] border border-[#1E293B] rounded-lg overflow-hidden" data-testid={`admin-category-${cat.slug}`}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#1E293B] bg-[#0f1830]">
              <span className="text-sm font-semibold text-white">{cat.name} <span className="text-slate-500 font-normal">({cat.services?.length || 0})</span></span>
              <div className="flex gap-2">
                <button onClick={() => openCat(cat)} data-testid={`edit-category-${cat.slug}`} className="p-1.5 text-slate-400 hover:text-[#F97316]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteCat(cat.id)} data-testid={`delete-category-${cat.slug}`} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="divide-y divide-[#1E293B]">
              {(cat.services || []).map((s) => (
                <div key={s.id} className="flex items-center justify-between px-5 py-3" data-testid={`admin-service-${s.slug}`}>
                  <span className="text-xs text-slate-300">{s.title}</span>
                  <div className="flex gap-2">
                    <button onClick={() => openSvc(s)} data-testid={`edit-service-${s.slug}`} className="p-1.5 text-slate-400 hover:text-[#F97316]"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteSvc(s.id)} data-testid={`delete-service-${s.slug}`} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
